import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, Zap, Infinity, RefreshCw, ExternalLink } from 'lucide-react';
import { usePremium } from '@/context/PremiumContext';

const UpgradeModal = () => {
  const { 
    showUpgradeModal, 
    setShowUpgradeModal, 
    upgradeToPremium, 
    restorePurchase,
    PREMIUM_PRICE,
    PREMIUM_PERIOD,
    DAILY_FREE_LIMIT
  } = usePremium();

  const handleSubscribe = () => {
    // Check if running in iOS app with StoreKit
    if (window.webkit?.messageHandlers?.nexiApp) {
      // Running in iOS app - trigger native StoreKit purchase
      window.webkit.messageHandlers.nexiApp.postMessage("showSubscription");
      setShowUpgradeModal(false);
    } else {
      // Web fallback (for testing or web-only users)
      localStorage.setItem('nexi_premium_purchased', 'true');
      upgradeToPremium();
    }
  };

  const handleRestore = () => {
    // Check if running in iOS app
    if (window.webkit?.messageHandlers?.nexiApp) {
      // Trigger native StoreKit restore
      window.webkit.messageHandlers.nexiApp.postMessage("restorePurchase");
      // The native app will call window.nexiRestoreSuccess() or window.nexiRestoreFailed()
    } else {
      // Web fallback - check localStorage
      const restored = restorePurchase();
      if (!restored) {
        alert('No previous subscription found. If you subscribed through the iOS app, please open the app to restore.');
      }
    }
  };

  return (
    <AnimatePresence>
      {showUpgradeModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setShowUpgradeModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-b from-[#1a0a2e] to-[#0a0118] border-t sm:border border-purple-500/30 rounded-t-3xl sm:rounded-2xl p-6 shadow-2xl w-full sm:max-w-md sm:mx-4 safe-area-bottom max-h-[90vh] overflow-y-auto"
          >
            {/* Close Button */}
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full"
              data-testid="close-upgrade-modal"
            >
              <X size={24} className="text-white/70" />
            </button>

            {/* Crown Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg shadow-yellow-500/30">
                <Crown size={32} className="text-white" />
              </div>
            </div>

            {/* Subscription Title - Required by Apple */}
            <h2 className="text-2xl font-bold text-center text-white mb-1">
              Nexi AI Premium
            </h2>
            
            <p className="text-center text-white/60 mb-4 text-sm">
              Unlock unlimited AI-powered assistance
            </p>

            {/* Features */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-3 text-white/90">
                <div className="w-7 h-7 bg-green-500/20 rounded-full flex items-center justify-center">
                  <Infinity size={14} className="text-green-400" />
                </div>
                <span className="text-sm">Unlimited questions per day</span>
              </div>
              <div className="flex items-center gap-3 text-white/90">
                <div className="w-7 h-7 bg-blue-500/20 rounded-full flex items-center justify-center">
                  <Zap size={14} className="text-blue-400" />
                </div>
                <span className="text-sm">Priority AI responses</span>
              </div>
              <div className="flex items-center gap-3 text-white/90">
                <div className="w-7 h-7 bg-purple-500/20 rounded-full flex items-center justify-center">
                  <RefreshCw size={14} className="text-purple-400" />
                </div>
                <span className="text-sm">Cancel anytime</span>
              </div>
            </div>

            {/* Subscription Details Box - Required by Apple Guideline 3.1.2 */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4">
              <h3 className="text-white font-semibold text-sm mb-2">Subscription Details</h3>
              <div className="space-y-1 text-xs text-white/70">
                <p><span className="text-white/50">Name:</span> Nexi AI Premium Monthly</p>
                <p><span className="text-white/50">Duration:</span> 1 Month (Auto-renewable)</p>
                <p><span className="text-white/50">Price:</span> {PREMIUM_PRICE} per {PREMIUM_PERIOD}</p>
              </div>
            </div>

            {/* Price Display */}
            <div className="text-center mb-4">
              <span className="text-3xl font-bold text-white">{PREMIUM_PRICE}</span>
              <span className="text-white/50 ml-1">/{PREMIUM_PERIOD}</span>
            </div>

            {/* Subscribe Button */}
            <motion.button
              onClick={handleSubscribe}
              className="w-full py-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-black font-bold text-base rounded-xl shadow-lg shadow-yellow-500/30"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="subscribe-button"
            >
              Subscribe Now
            </motion.button>

            {/* Restore Purchase */}
            <button
              onClick={handleRestore}
              className="w-full mt-2 py-2 text-white/50 text-sm hover:text-white/70 transition-colors"
              data-testid="restore-purchase-button"
            >
              Restore Purchase
            </button>

            {/* Free Option */}
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="w-full mt-1 py-2 text-white/40 text-sm hover:text-white/60 transition-colors"
              data-testid="continue-free-button"
            >
              Continue with {DAILY_FREE_LIMIT} free/day
            </button>
            
            {/* Legal Info - Required by Apple Guideline 3.1.2 */}
            <div className="border-t border-white/10 mt-4 pt-4">
              <p className="text-center text-white/40 text-xs mb-3">
                Payment will be charged to your Apple ID account at confirmation of purchase. 
                Subscription automatically renews unless canceled at least 24 hours before the end of the current period. 
                Your account will be charged for renewal within 24 hours prior to the end of the current period. 
                You can manage and cancel your subscriptions in your App Store account settings.
              </p>
              
              {/* Privacy & Terms Links - Required by Apple */}
              <div className="flex justify-center gap-4 text-xs">
                <a 
                  href="/privacy" 
                  className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                  onClick={() => setShowUpgradeModal(false)}
                >
                  Privacy Policy <ExternalLink size={10} />
                </a>
                <a 
                  href="/terms" 
                  className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                  onClick={() => setShowUpgradeModal(false)}
                >
                  Terms of Use <ExternalLink size={10} />
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UpgradeModal;
